/**
 * Shared research contracts used by the Office add-ins. This is the single
 * source of truth for:
 *
 *  - the public brand context (Finster vs FactSet AI naming / labels and the
 *    brand-specific public tool name `finster_query` vs `factset_ai_query`),
 *  - full and focused research tool names and the `{ query }` input schema,
 *  - the streaming and buffered HTTP calls to the internal data agent.
 *
 * Each surface keeps its own natural-language tool *description* (the deck
 * builder and the spreadsheet agent want different guidance) and its own
 * delivery channel: PowerPoint additionally streams sub-events to its chat
 * timeline (`powerpoint/api/finster/queries.ts::queryFinsterStream`), while the
 * Excel executor loop consumes a single buffered tool result. The request
 * payload, data-source contract, and branding are identical via this module.
 */

import { buildApiUrl } from "../../config";
import { withCredentials } from "../../apiClient";
import { fetchWithRetry } from "../../utils/http";
import { checkAuthStatus } from "../../utils/authStatus";
import { parseErrorResponse } from "../../utils/apiError";
import { isAbortError } from "../../utils/abortError";
import { withLangfuseMetadataHeader } from "../../services/langfuseTrace";
import { isFinsterChatSessionId } from "../../utils/sessionId";
import { appendUserFilesDataSource } from "../../researchSelection";
import {
  AGENT_FLAG,
  FINSTER_QUERY_MODE_AGENT_FLAGS,
  type FinsterQueryDataSource,
  type FinsterQueryMode,
  type PublicDataSourceBrandName,
} from "./constants";

// ---------------------------------------------------------------------------
// Brand context
// ---------------------------------------------------------------------------

/** User-visible brand of the research agent. */
export type PublicBrandName = PublicDataSourceBrandName;

/** Stable caller attribution accepted by the add-in BFF. */
export type FinsterClientOrigin = "powerpoint_addin" | "excel_addin";

/** Backend limits from data-feed's PowerPointClientContext model. */
export const FINSTER_CONTEXT_IDENTIFIER_MAX_LENGTH = 256;
export const FINSTER_CONTEXT_DISPLAY_TEXT_MAX_LENGTH = 512;

/** Host metadata accepted alongside a matching office add-in client_origin. */
export interface PowerPointClientContext {
  presentation_id?: string;
  presentation_name?: string;
  slide_id?: string;
  slide_number?: number;
  slide_title?: string;
}

export interface ExcelClientContext {
  workbook_id?: string;
  workbook_name?: string;
  sheet_id?: string;
  sheet_name?: string;
  sheet_number?: number;
}

export type OfficeClientContext = PowerPointClientContext | ExcelClientContext;

/** Brand-specific public name of the research tool exposed to the model. */
export type PublicResearchToolName = "finster_query" | "factset_ai_query";

export const FINSTER_RESEARCH_TOOL_NAME: PublicResearchToolName = "finster_query";
export const FACTSET_RESEARCH_TOOL_NAME: PublicResearchToolName = "factset_ai_query";
export const FILINGS_QUERY_TOOL_NAME = "filings_query" as const;
export type FilingsQueryToolName = typeof FILINGS_QUERY_TOOL_NAME;

/**
 * Brand-dependent display strings for the research tool. Carries every label
 * either surface needs; surfaces use the subset relevant to them (Excel uses
 * the tool name + status / result labels; PowerPoint additionally uses the
 * citation-markup and task labels).
 */
export interface FinsterBrandContext {
  publicBrandName: PublicBrandName;
  researchToolName: PublicResearchToolName;
  researchToolDescriptionBrand: PublicBrandName;
  researchToolDataLabel: string;
  researchStatusLabel: string;
  researchResultLabel: string;
  taskContextHeaderBrand: "FINSTER" | "FACTSET AI";
  taskRunOutputLabel: string;
  citationMarkupLabel: string;
}

const FINSTER_BRAND_CONTEXT: FinsterBrandContext = {
  publicBrandName: "Finster",
  researchToolName: FINSTER_RESEARCH_TOOL_NAME,
  researchToolDescriptionBrand: "Finster",
  researchToolDataLabel: "Finster Data",
  researchStatusLabel: "Querying Finster",
  researchResultLabel: "Finster Data",
  taskContextHeaderBrand: "FINSTER",
  taskRunOutputLabel: "Finster task run output",
  citationMarkupLabel: "Finster citation markup",
};

const FACTSET_BRAND_CONTEXT: FinsterBrandContext = {
  publicBrandName: "FactSet AI",
  researchToolName: FACTSET_RESEARCH_TOOL_NAME,
  researchToolDescriptionBrand: "FactSet AI",
  researchToolDataLabel: "FactSet AI",
  researchStatusLabel: "Querying FactSet AI",
  researchResultLabel: "FactSet AI",
  taskContextHeaderBrand: "FACTSET AI",
  taskRunOutputLabel: "FactSet AI task run output",
  citationMarkupLabel: "FactSet AI citation markup",
};

/**
 * Resolve the brand context. Defaults to Finster; selects FactSet AI when the
 * surface is running inside the FactSet environment. An explicit
 * `researchToolName` override wins over the brand default (kept for parity with
 * the PowerPoint policy surface).
 */
export function resolveFinsterBrandContext(
  publicBrandName?: PublicBrandName | null,
  researchToolName?: PublicResearchToolName | null,
): FinsterBrandContext {
  const base =
    publicBrandName === "FactSet AI" ? FACTSET_BRAND_CONTEXT : FINSTER_BRAND_CONTEXT;
  return {
    ...base,
    researchToolName: researchToolName ?? base.researchToolName,
  };
}

/** Type guard: is `tool` one of the brand-specific research tool names? */
export function isResearchToolName(tool: string): tool is PublicResearchToolName {
  return tool === FINSTER_RESEARCH_TOOL_NAME || tool === FACTSET_RESEARCH_TOOL_NAME;
}

/** Type guard for the focused filings/IR research route. */
export function isFilingsQueryToolName(tool: string): tool is FilingsQueryToolName {
  return tool === FILINGS_QUERY_TOOL_NAME;
}

/** Type guard covering the full and focused research routes. */
export function isAnyResearchToolName(
  tool: string,
): tool is PublicResearchToolName | FilingsQueryToolName {
  return isResearchToolName(tool) || isFilingsQueryToolName(tool);
}

// ---------------------------------------------------------------------------
// Model-facing input schema (shared)
// ---------------------------------------------------------------------------

/**
 * Canonical input schema for the research tool. Both surfaces expose the same
 * single required `query` string; only the surrounding description differs.
 */
export const FINSTER_QUERY_INPUT_SCHEMA = {
  type: "object",
  properties: {
    query: {
      type: "string",
      description:
        "Natural-language research question (e.g. \"Summarize NVIDIA's most " +
        "recent earnings call\", \"Summarize a company's latest SEC filing\", " +
        "\"Who are Datadog's main competitors?\").",
    },
  },
  required: ["query"],
  additionalProperties: false,
} as const;

// ---------------------------------------------------------------------------
// Query execution core
// ---------------------------------------------------------------------------

/**
 * Tool-scoped timeout for a single `finster_query` call. The data agent is
 * multi-hop and can take several minutes, so allow the full per-request
 * `fetchWithRetry` budget.
 */
export const FINSTER_QUERY_TIMEOUT_MS = 10 * 60 * 1000;
const MAX_QUERY_USER_FILE_IDS = 1000;
const MAX_QUERY_USER_FILE_ID_LENGTH = 1024;
const INCLUDE_SESSION_HISTORY = false;

const SESSION_ID_KEY = "session_id";
const SESSION_NAME_KEY = "session_name";

/**
 * Older data-feed builds may 422 on unknown/unsupported attribution fields.
 * Treat those as non-fatal so research still works when attribution isn't
 * deployed yet — chat grouping just won't apply for that query.
 */
export function isClientAttributionRejection(
  status: number,
  errorText: string
): boolean {
  if (status !== 422) return false;
  const normalized = errorText.toLowerCase();
  return (
    normalized.includes("client_origin") ||
    normalized.includes("client_context")
  );
}

/**
 * Reads and trims a string field from an API record.
 */
function getTrimmedSessionString(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return undefined;
}

/**
 * Pull canonical Finster session metadata out of a query response or SSE frame.
 * We intentionally only read the snake_case API-contract keys (`session_id` /
 * `session_name`) so Excel and PowerPoint share the same extraction logic.
 */
export function extractFinsterSession(source: unknown): { id?: string; name?: string } {
  if (!source || typeof source !== "object") return {};
  const record = source as Record<string, unknown>;
  const id = getTrimmedSessionString(record, SESSION_ID_KEY);
  const name = getTrimmedSessionString(record, SESSION_NAME_KEY);
  return { ...(id ? { id } : {}), ...(name ? { name } : {}) };
}

export interface FinsterQueryExecResult {
  success: boolean;
  answer?: string;
  citations?: unknown;
  error?: string;
  /** Upstream Finster/FactSet session id (`session_id`), for trace correlation. */
  sessionId?: string;
  /** Upstream Finster/FactSet session name (`session_name`), for trace correlation. */
  sessionName?: string;
}

export interface ExecuteFinsterQueryParams {
  query: string;
  /** Office host that owns this query for downstream trace attribution. */
  deliveryChannel: "excel" | "powerpoint";
  /**
   * Chat-tab id (ULID) shared by every turn in this tab. The add-in server
   * forwards it as the upstream `session_id`, so follow-up prompts append to
   * one Finster Chat session instead of each minting a new one.
   */
  chatSessionId?: string;
  /** Host surface making the request, forwarded for server-side attribution. */
  clientOrigin?: FinsterClientOrigin;
  /** Optional host workbook/presentation metadata for session attribution. */
  clientContext?: OfficeClientContext;
  /**
   * Data sources to request. `undefined` omits the constraint even when files
   * are attached; `[]` is an explicit empty scope (files-only once IDs are
   * present). Callers remain responsible for entitlement filtering.
   */
  dataSources?: readonly FinsterQueryDataSource[];
  /** User-uploaded file ids to scope retrieval to. */
  userFileIds?: readonly string[];
  /** Exact resolved Skill guidance for the nested model call. Never merge into `query`. */
  selectedSkillsGuidance?: string;
  /** User-visible brand sent to the agent. Defaults to "Finster". */
  publicBrandName?: PublicBrandName;
  /** Agent mode. Defaults to "thinking" (multi-hop). */
  mode?: FinsterQueryMode;
  /** Cancellation signal from the surrounding tool loop. */
  signal?: AbortSignal;
  /** Tool-scoped timeout. Defaults to {@link FINSTER_QUERY_TIMEOUT_MS}. */
  timeoutMs?: number;
  /** Office turn metadata used only by the trusted add-in server proxy. */
  langfuseMetadata?: Record<string, unknown>;
}

function validateSelectedSkillsGuidance(guidance: unknown): string | undefined {
  if (guidance === undefined) return undefined;
  if (typeof guidance !== "string" || !guidance.trim()) {
    return "Selected Skill guidance must be a non-empty string.";
  }
  return undefined;
}

/** Omitted sources stay unrestricted even when files are attached. */
function resolveRequestDataSources(
  dataSources: readonly FinsterQueryDataSource[] | undefined,
  hasUserFiles: boolean
): string[] | undefined {
  if (dataSources === undefined) return undefined;
  return appendUserFilesDataSource(dataSources, hasUserFiles);
}

/**
 * Normalized sub-events emitted while the Finster/FactSet agent stream is
 * running. Consumers use these to render nested reasoning, tool calls, answer
 * deltas, and the final cited response without depending on raw SSE frame names.
 */
export type FinsterStreamSubEvent =
  | { type: "status"; status: string }
  | { type: "session"; sessionName?: string }
  | { type: "reasoning"; chunk: string; hopIndex: number }
  | {
      type: "tool";
      toolName: string;
      status: "calling" | "completed" | "failed";
      callId?: string;
      hopIndex: number;
      description?: string;
      sectionTitle?: string;
    }
  | { type: "answerDelta"; chunk: string }
  | { type: "final"; answer: string; citations?: unknown[] };

/**
 * Executes the existing research tool through the buffered JSON endpoint.
 * This is used only when one executor turn contains multiple independent
 * research calls; standalone calls keep the richer streaming timeline.
 */
export async function executeFinsterQueryBuffered(
  params: ExecuteFinsterQueryParams,
): Promise<FinsterQueryExecResult> {
  const {
    query,
    deliveryChannel,
    chatSessionId,
    clientOrigin,
    clientContext,
    dataSources,
    userFileIds = [],
    selectedSkillsGuidance,
    publicBrandName = "Finster",
    mode = "thinking",
    signal,
    timeoutMs = FINSTER_QUERY_TIMEOUT_MS,
    langfuseMetadata,
  } = params;
  const agentFlag = FINSTER_QUERY_MODE_AGENT_FLAGS[mode] ?? AGENT_FLAG;
  const selectedSkillsGuidanceError = validateSelectedSkillsGuidance(selectedSkillsGuidance);
  if (selectedSkillsGuidanceError) {
    return { success: false, error: selectedSkillsGuidanceError };
  }

  if (userFileIds.length > MAX_QUERY_USER_FILE_IDS) {
    return {
      success: false,
      error: `Your Files supports at most ${MAX_QUERY_USER_FILE_IDS} selected files per query.`,
    };
  }
  if (
    userFileIds.some(
      (fileId) =>
        typeof fileId !== "string" ||
        !fileId.trim() ||
        fileId.trim().length > MAX_QUERY_USER_FILE_ID_LENGTH
    )
  ) {
    return {
      success: false,
      error: "The selected Your Files scope contains an invalid file identifier.",
    };
  }

  const normalizedUserFileIds = Array.from(
    new Set(userFileIds.map((fileId) => fileId.trim()))
  );
  const requestDataSources = resolveRequestDataSources(
    dataSources,
    normalizedUserFileIds.length > 0
  );

  // `session_id` stays outside the attribution block: the 422 retry below drops
  // client_origin/client_context, and session continuity must survive that.
  const buildBody = (includeAttribution: boolean) =>
    JSON.stringify({
      query,
      ...(selectedSkillsGuidance !== undefined
        ? { selected_skills_guidance: selectedSkillsGuidance }
        : {}),
      ...(isFinsterChatSessionId(chatSessionId)
        ? { session_id: chatSessionId.trim() }
        : {}),
      include_session_history: INCLUDE_SESSION_HISTORY,
      ...(includeAttribution && clientOrigin
        ? {
            client_origin: clientOrigin,
            ...(hasClientContextValues(clientContext)
              ? { client_context: clientContext }
              : {}),
          }
        : {}),
      ...(requestDataSources !== undefined ? { data_sources: requestDataSources } : {}),
      ...(normalizedUserFileIds.length > 0
        ? { user_file_ids: normalizedUserFileIds }
        : {}),
      agent_flag: agentFlag,
      output_mode: "markdown",
      publicBrandName,
    });
  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
  const onCallerAbort = () => timeoutController.abort();
  if (signal) {
    if (signal.aborted) timeoutController.abort();
    else signal.addEventListener("abort", onCallerAbort, { once: true });
  }

  try {
    const fetchBuffered = (includeAttribution: boolean) =>
      fetchWithRetry(
        buildApiUrl("/api/finster/query"),
        withLangfuseMetadataHeader(
          withCredentials({
            method: "POST",
            headers: {
              "X-Finster-Office-Host": deliveryChannel,
            },
            body: buildBody(includeAttribution),
            signal: timeoutController.signal,
          }),
          langfuseMetadata,
        ),
        1,
        "FINSTER_QUERY",
      );

    let response = await fetchBuffered(true);

    checkAuthStatus(response);
    if (!response.ok) {
      const error = await parseErrorResponse(response);
      if (isClientAttributionRejection(response.status, error)) {
        console.warn(
          "[FinsterQuery] Attribution rejected by upstream; retrying without client_origin/client_context"
        );
        response = await fetchBuffered(false);
        checkAuthStatus(response);
        if (!response.ok) {
          return { success: false, error: await parseErrorResponse(response) };
        }
      } else {
        return { success: false, error };
      }
    }

    const data = await response.json() as Record<string, unknown>;
    const answer =
      typeof data.output === "string"
        ? data.output
        : typeof data.answer === "string"
          ? data.answer
          : "";
    if (!answer.trim()) {
      return { success: false, error: "Finster query returned an empty response." };
    }
    const session = extractFinsterSession(data);
    return {
      success: true,
      answer,
      citations: Array.isArray(data.citations) ? data.citations : undefined,
      ...(session.id ? { sessionId: session.id } : {}),
      ...(session.name ? { sessionName: session.name } : {}),
    };
  } catch (err) {
    if (signal?.aborted) throw err;
    if (isAbortError(err)) {
      return {
        success: false,
        error: `finster_query timed out after ${Math.round(timeoutMs / 1000)}s.`,
      };
    }
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCallerAbort);
  }
}

/**
 * Maps backend tool status strings into the UI's three supported states.
 */
function normalizeFinsterToolStatus(raw: unknown): "calling" | "completed" | "failed" {
  const value = typeof raw === "string" ? raw.toLowerCase() : "";
  if (value === "completed") return "completed";
  if (value === "failed" || value === "error") return "failed";
  return "calling";
}

function hasClientContextValues(
  clientContext: OfficeClientContext | undefined
): clientContext is OfficeClientContext {
  if (!clientContext) return false;
  for (const key in clientContext) {
    const value = clientContext[key as keyof OfficeClientContext];
    if (value !== undefined && value !== null && value !== "") {
      return true;
    }
  }
  return false;
}

/**
 * Executes the streaming Finster/FactSet research endpoint and buffers the final
 * model-facing result while forwarding normalized sub-events to the caller.
 */
export async function executeFinsterQueryStream(
  params: ExecuteFinsterQueryParams & {
    onSubEvent?: (event: FinsterStreamSubEvent) => void;
  },
): Promise<FinsterQueryExecResult> {
  if (typeof ReadableStream === "undefined") {
    return { success: false, error: "Finster streaming is not available in this environment." };
  }

  const {
    query,
    deliveryChannel,
    chatSessionId,
    clientOrigin,
    clientContext,
    dataSources,
    userFileIds = [],
    selectedSkillsGuidance,
    publicBrandName = "Finster",
    mode = "thinking",
    signal,
    timeoutMs = FINSTER_QUERY_TIMEOUT_MS,
    langfuseMetadata,
    onSubEvent = () => {},
  } = params;
  const agentFlag = FINSTER_QUERY_MODE_AGENT_FLAGS[mode] ?? AGENT_FLAG;
  const selectedSkillsGuidanceError = validateSelectedSkillsGuidance(selectedSkillsGuidance);
  if (selectedSkillsGuidanceError) {
    return { success: false, error: selectedSkillsGuidanceError };
  }
  if (userFileIds.length > MAX_QUERY_USER_FILE_IDS) {
    return {
      success: false,
      error: `Your Files supports at most ${MAX_QUERY_USER_FILE_IDS} selected files per query.`,
    };
  }
  if (
    userFileIds.some(
      (fileId) =>
        typeof fileId !== "string" ||
        !fileId.trim() ||
        fileId.trim().length > MAX_QUERY_USER_FILE_ID_LENGTH
    )
  ) {
    return {
      success: false,
      error: "The selected Your Files scope contains an invalid file identifier.",
    };
  }
  const normalizedUserFileIds = Array.from(
    new Set(userFileIds.map((fileId) => fileId.trim()))
  );
  const requestDataSources = resolveRequestDataSources(
    dataSources,
    normalizedUserFileIds.length > 0
  );

  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
  const onCallerAbort = () => timeoutController.abort();
  if (signal) {
    if (signal.aborted) timeoutController.abort();
    else signal.addEventListener("abort", onCallerAbort, { once: true });
  }

  try {
    // `session_id` stays outside the attribution block: the 422 retry below
    // drops client_origin/client_context, and continuity must survive that.
    const buildBody = (includeAttribution: boolean) =>
      JSON.stringify({
        query,
        ...(selectedSkillsGuidance !== undefined
          ? { selected_skills_guidance: selectedSkillsGuidance }
          : {}),
        ...(isFinsterChatSessionId(chatSessionId)
        ? { session_id: chatSessionId.trim() }
        : {}),
        include_session_history: INCLUDE_SESSION_HISTORY,
        ...(includeAttribution && clientOrigin
          ? {
              client_origin: clientOrigin,
              ...(hasClientContextValues(clientContext)
                ? { client_context: clientContext }
                : {}),
            }
          : {}),
        ...(requestDataSources !== undefined ? { data_sources: requestDataSources } : {}),
        ...(normalizedUserFileIds.length > 0
          ? { user_file_ids: normalizedUserFileIds }
          : {}),
        agent_flag: agentFlag,
        output_mode: "markdown",
        publicBrandName,
      });

    const fetchStream = (includeAttribution: boolean) =>
      fetchWithRetry(
        buildApiUrl("/api/finster/query/stream"),
        withLangfuseMetadataHeader(
          withCredentials({
            method: "POST",
            headers: {
              "X-Finster-Office-Host": deliveryChannel,
            },
            body: buildBody(includeAttribution),
            signal: timeoutController.signal,
          }),
          langfuseMetadata,
        ),
        1,
        "FINSTER_QUERY_STREAM",
      );

    let response = await fetchStream(true);

    checkAuthStatus(response);
    if (!response.ok) {
      const error = await parseErrorResponse(response);
      if (isClientAttributionRejection(response.status, error)) {
        console.warn(
          "[FinsterQuery] Attribution rejected by upstream; retrying without client_origin/client_context"
        );
        response = await fetchStream(false);
        checkAuthStatus(response);
        if (!response.ok) {
          return { success: false, error: await parseErrorResponse(response) };
        }
      } else {
        return { success: false, error };
      }
    }
    if (!response.body) {
      return { success: false, error: "Finster stream returned no response body." };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = "";
    let currentEvent: string | null = null;
    let liveAnswer = "";
    let finalAnswer = "";
    let citations: unknown[] | undefined;
    let sessionId: string | undefined;
    let sessionName: string | undefined;

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      const lines = sseBuffer.split("\n");
      sseBuffer = lines.pop() || "";
      for (const raw of lines) {
        const line = raw.trim();
        if (!line) {
          currentEvent = null;
          continue;
        }
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim();
          continue;
        }
        if (!line.startsWith("data:")) continue;
        const dataStr = line.slice(5).trim();
        if (!dataStr || dataStr === "[DONE]") continue;
        let evt: Record<string, unknown>;
        try {
          evt = JSON.parse(dataStr) as Record<string, unknown>;
        } catch {
          continue;
        }
        const type = currentEvent || (evt.event as string) || (evt.type as string) || "";
        if (type === "status") {
          onSubEvent({ type: "status", status: String(evt.status ?? "") });
        } else if (type === "session") {
          const found = extractFinsterSession(evt);
          if (!sessionId && found.id) sessionId = found.id;
          if (!sessionName && found.name) sessionName = found.name;
          onSubEvent({ type: "session", sessionName: found.name });
        } else if (type === "reasoningChunk") {
          onSubEvent({
            type: "reasoning",
            chunk: String(evt.chunk ?? ""),
            hopIndex: Number(evt.hop_index ?? evt.iteration ?? 0),
          });
        } else if (type === "toolCallStatus") {
          onSubEvent({
            type: "tool",
            toolName: String(evt.tool_name ?? ""),
            status: normalizeFinsterToolStatus(evt.status),
            callId: evt.call_id as string | undefined,
            hopIndex: Number(evt.hop_index ?? 0),
            description: evt.description as string | undefined,
            sectionTitle: evt.section_title as string | undefined,
          });
        } else if (type === "llmChunk" || type === "textChunk") {
          const chunk = String(evt.chunk ?? "");
          liveAnswer += chunk;
          if (chunk) onSubEvent({ type: "answerDelta", chunk });
        } else if (type === "agentResponse") {
          const agent = (evt.agent as Record<string, unknown>) || evt;
          const out = (agent.output as string) || (evt.output as string) || "";
          if (out) {
            finalAnswer = out;
            citations =
              (agent.citations as unknown[]) || (evt.citations as unknown[]) || citations;
          }
          const agentSession = extractFinsterSession(agent);
          const evtSession = extractFinsterSession(evt);
          if (!sessionId) sessionId = agentSession.id ?? evtSession.id;
          if (!sessionName) sessionName = agentSession.name ?? evtSession.name;
        }
      }
    }

    const answer = finalAnswer || liveAnswer;
    if (!answer) {
      return { success: false, error: "Finster stream returned an empty response." };
    }
    onSubEvent({ type: "final", answer, citations });
    return {
      success: true,
      answer,
      citations,
      ...(sessionId ? { sessionId } : {}),
      ...(sessionName ? { sessionName } : {}),
    };
  } catch (err) {
    if (signal?.aborted) {
      throw err;
    }
    if (isAbortError(err)) {
      return {
        success: false,
        error: `finster_query timed out after ${Math.round(timeoutMs / 1000)}s.`,
      };
    }
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onCallerAbort);
  }
}
